import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  fundPeriodsQueryOptions,
  industryHoldingsQueryOptions,
  monthlyHoldingsQueryOptions,
  sectorCompaniesQueryOptions,
} from '@/features/mutual-funds/api/queries'
import { knownPeriod, sectorOrAll } from '@/features/mutual-funds/lib/defaults'
import type { RouterContext } from '@/routes/layouts/root'
import { pagingParams, periodParamSchema, sectorParamSchema } from '@/screens/mutual-funds/search'

/**
 * - `period` — a `/periods/` label; absent means the latest.
 * - `sector` — the sector CODE whose companies the table lists; absent, OR
 *   naming no classified slice in the resolved period (a previous period's
 *   code, still sitting in the URL after a period change), means All sectors
 *   (`sectorOrAll`). Never the unclassified slice, which has no code.
 * - `page`/`pageSize` — the companies table, sliced client-side over one
 *   response (`pagingParams`'s own doc). Reset to page 1 whenever a `period`
 *   or `sector` is picked, since both change which companies list is in view.
 */
export const industryHoldingsSearchSchema = z.object({
  period: periodParamSchema,
  sector: sectorParamSchema,
  ...pagingParams,
})
export type IndustryHoldingsSearch = z.infer<typeof industryHoldingsSearchSchema>

/**
 * The `/mutual-funds/industry-holdings` loader.
 *
 * MAIN: the period list (the month picker) and the sector split for the entry
 * URL's period (the donut and its legend). The split is keyed on `period`, so
 * the screen reads it with `useIndustryHoldings` (`keepPreviousData`), not
 * suspense. With no `?period=` (the usual entry) the two run together; a URL
 * that names one waits for the list first, since a label it does not hold is a
 * 400 and reads as the latest instead (`knownPeriod`).
 *
 * SECONDARY, after the split lands and only for a real navigation: the
 * companies table — every sector's (`/monthly-holdings/`), or the URL's
 * sector's once the split confirms the period has it.
 */
export async function loadIndustryHoldings({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = industryHoldingsSearchSchema.parse(location.search)
  const periodsRead = ensureRouteData(queryClient, fundPeriodsQueryOptions())
  const period = search.period === undefined ? null : knownPeriod(search.period, await periodsRead)

  const [, holdings] = await Promise.all([
    periodsRead,
    ensureRouteData(queryClient, industryHoldingsQueryOptions(period)),
  ])

  if (preload) return
  // Mirrors `IndustryHoldingsScreen`'s own fallback exactly, so this prefetch and the screen's
  // table read always land on the same key.
  const sector = sectorOrAll(search.sector, holdings.sectors)
  if (sector === null) prefetchRouteData(queryClient, monthlyHoldingsQueryOptions(period))
  else prefetchRouteData(queryClient, sectorCompaniesQueryOptions(sector, period))
}
